// "Pessoa da equipe" — o campo que diz QUEM recebeu uma despesa de remuneração
// (05/10/2026). Usado na Nova despesa e no detalhe do Contas a Pagar.
//
// Sem a pessoa, a remuneração não é dividida pelo rateio da ficha (Lucas 70/30,
// Marina 100% Administrativo) nem vira custo por hora nos contratos. Quem ligava
// era o sync do Conta Azul, morto desde 15/07/2026; R$ 19,9 mil de set/2026
// nasceram sem pessoa. Regras em utils/pessoaDaDespesa.ts (espelho do servidor).
//
// O campo só aparece quando faz sentido: categoria de remuneração de gente da
// equipe, ou despesa que já tem pessoa (para poder ver e trocar).
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Form, Select, Button, Typography } from 'antd';
import { API_URL } from '../../config';
import { getToken } from './utils';
import {
  type Ficha, NINGUEM, pedeAPessoa, opcoesDePessoa, textoDaPessoa, podeLimparPessoa,
} from '../../utils/pessoaDaDespesa';

const { Text } = Typography;

/** Carrega as fichas (equipe atual e quem já saiu). `erro` ≠ lista vazia.
 *  `ligado` = o campo está (ou vai ficar) na tela; enquanto não, nada é pedido
 *  ao servidor — o detalhe de uma conta de luz não precisa da lista da equipe. */
export function useFichasDaEquipe(ligado = true) {
  const [fichas, setFichas] = useState<Ficha[]>([]);
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [pedido, setPedido] = useState(false);
  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/profissionais?incluir_inativos=1`, { headers: { Authorization: token } });
      const d = r.data && r.data.body ? JSON.parse(r.data.body) : r.data;
      if (!Array.isArray(d)) throw new Error('resposta inesperada');
      setFichas(d.map((p: any) => ({ id: p.id, nome_canonical: p.nome_canonical, ativo: !!p.ativo,
        aliases: Array.isArray(p.aliases) ? p.aliases : [] })));
      setEstado('ok');
    } catch { setEstado('erro'); }
  }, []);
  useEffect(() => {
    if (!ligado || pedido) return;
    setPedido(true);
    carregar();
  }, [ligado, pedido, carregar]);
  return { fichas, estado, recarregar: carregar };
}

export function PessoaDaEquipe({ linha, escolha, sugerida, faltando, fichas, estado, recarregar, onEscolher, desabilitado, rotuloAtual, cobrar }: {
  linha: string;
  /** '' = nada escolhido · NINGUEM = "não é da equipe" · id da ficha */
  escolha: string;
  /** a escolha atual veio do nome do fornecedor (a pessoa ainda não mexeu) */
  sugerida: boolean;
  /** tentou salvar sem escolher: o campo fica vermelho */
  faltando?: boolean;
  fichas: Ficha[];
  estado: 'carregando' | 'ok' | 'erro';
  recarregar: () => void;
  onEscolher: (escolha: string) => void;
  desabilitado?: boolean;
  /** nome da pessoa já gravada — para o campo não mostrar um identificador
   *  enquanto a lista carrega ou se ela falhar */
  rotuloAtual?: string | null;
  /** a tela NÃO salva sem a escolha (Nova despesa) — só então o campo leva o
   *  asterisco de obrigatório; no detalhe ele avisa, mas não trava */
  cobrar?: boolean;
}) {
  if (!pedeAPessoa(linha) && !escolha) return null;
  const texto = textoDaPessoa({ linha, escolha, sugerida });
  const semEscolha = pedeAPessoa(linha) && !escolha;
  return (
    <Form.Item label="Pessoa da equipe" required={!!cobrar && pedeAPessoa(linha)}
      validateStatus={faltando ? 'error' : (semEscolha && estado === 'ok' ? 'warning' : undefined)}
      extra={estado === 'erro'
        ? <Text type="danger" style={{ fontSize: 11 }}>
            Não consegui carregar a lista da equipe.{' '}
            <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 11 }} onClick={recarregar}>Tentar de novo</Button>
            {pedeAPessoa(linha) && ' Se salvar assim, o sistema tenta achar a pessoa pelo nome do fornecedor.'}
          </Text>
        : (texto ? <Text type={semEscolha ? 'warning' : 'secondary'} style={{ fontSize: 11 }}>{texto}</Text> : undefined)}>
      <Select
        value={escolha || undefined}
        onChange={(v) => onEscolher(v || '')}
        placeholder={estado === 'carregando' ? 'Carregando a equipe…' : estado === 'erro' ? 'Lista indisponível' : 'Quem recebeu?'}
        disabled={desabilitado || estado !== 'ok'}
        loading={estado === 'carregando'}
        allowClear={podeLimparPessoa(linha)} showSearch optionFilterProp="label"
        aria-label="Pessoa da equipe"
        options={estado === 'ok' ? opcoesDePessoa(fichas)
          : (escolha && escolha !== NINGUEM && rotuloAtual ? [{ value: escolha, label: rotuloAtual }] : [])}
        style={{ width: '100%' }} />
    </Form.Item>
  );
}

export { NINGUEM };
